import React, { useEffect, useState } from 'react';
import { Button, Space, Tag, message } from 'antd';
import { MailOutlined, DownloadOutlined } from '@ant-design/icons';
import { useHasRole } from '../auth/RoleGuard';
import {
  fetchReceiptsForPayment,
  resendReceipt,
  downloadReceiptPdf,
  CustomerReceipt,
} from '../../services/api/receiptsApi';

interface ReceiptActionsProps {
  paymentId: number;
}

/**
 * Per-payment-row receipt controls for the staff Payment History table.
 *
 * A payment predates receipts, or never got one for another reason, is a
 * normal case — the row renders nothing rather than an empty placeholder.
 * Re-send is owner/advisor only, matching the server's `requireOwnerOrAdvisor`
 * on `POST /receipts/:id/resend`; Download follows the same role split as the
 * server's `requireStaff` on the pdf route, so a technician can still pull a
 * copy even though they can't trigger a re-send.
 */
const ReceiptActions: React.FC<ReceiptActionsProps> = ({ paymentId }) => {
  // UI convenience only — hiding the button saves a technician a wasted click,
  // but the actual authorization lives server-side (`requireOwnerOrAdvisor`).
  // A stale or bypassed client check here can never grant an unauthorized resend.
  const canResend = useHasRole(['owner', 'advisor']);
  const [receipt, setReceipt] = useState<CustomerReceipt | null>(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchReceiptsForPayment(paymentId)
      .then((rows) => {
        if (!cancelled) setReceipt(rows[0] ?? null);
      })
      .catch(() => {
        if (!cancelled) setReceipt(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [paymentId]);

  if (loading || !receipt) {
    return null;
  }

  const handleResend = async () => {
    setResending(true);
    try {
      const result = await resendReceipt(receipt.id);
      // Resend never throws on a failed send — `emailed: false` is the honest
      // outcome for, e.g., a customer with no email on file, not an error.
      if (result.emailed) {
        message.success('Receipt re-sent.');
      } else {
        message.warning('Saved, but the email did not go — check email settings');
      }
    } catch {
      message.error('Could not re-send that receipt.');
    } finally {
      setResending(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadReceiptPdf(receipt.id, receipt.receipt_number);
    } catch {
      message.error('Could not download that receipt.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Space size="small" wrap>
      <Tag>{receipt.receipt_number}</Tag>
      {canResend && (
        <Button
          type="link"
          size="small"
          icon={<MailOutlined />}
          loading={resending}
          onClick={handleResend}
        >
          Re-send
        </Button>
      )}
      <Button
        type="link"
        size="small"
        icon={<DownloadOutlined />}
        loading={downloading}
        onClick={handleDownload}
      >
        Download
      </Button>
    </Space>
  );
};

export default ReceiptActions;
